/**
 * Accent theme variables (INVENTORY §10).
 *
 * The prototype had no in-app theme switcher: `accent` was a design-time prop
 * and the four accent custom properties were written onto the app root div,
 * overriding the `:root` values in `gt-tokens.css`. `App.tsx` does the same.
 *
 * The magenta rule matters: `--acc2` is normally the fixed magenta 335, but
 * when the primary accent IS magenta it flips to green 150 so the ▲/▼ pair
 * stays distinguishable.
 *
 * There is no `accent` field in the store yet (`store/slices/ui.ts` does not
 * declare one, and this lane must not touch the store). When one lands, App
 * swaps its `ACCENT` constant for `useQd((s) => s.accent)` — nothing else
 * changes.
 */

import type { CSSProperties } from 'react';

export type Accent = 'green' | 'cyan' | 'magenta' | 'amber';

export const ACCENT_HUE: Record<Accent, number> = { green: 150, cyan: 195, magenta: 335, amber: 90 };

/** The four-var override, verbatim from prototype `renderVals()` lines 2235-2239. */
export function accentVars(accent: Accent = 'green'): CSSProperties {
  const hue = ACCENT_HUE[accent] ?? 150;
  const flip = accent === 'magenta';
  return {
    '--acc': 'oklch(0.85 0.19 ' + hue + ')',
    '--acc2': flip ? 'oklch(0.85 0.19 150)' : 'oklch(0.85 0.19 335)',
    '--accSoft': 'oklch(0.85 0.19 ' + hue + ' / 0.09)',
    '--acc2Soft': flip ? 'oklch(0.85 0.19 150 / .08)' : 'oklch(0.85 0.19 335 / .08)',
  } as CSSProperties;
}
